import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { WhatsAppStatus, ConnectionStatus } from '../../types/admin';
import { adminApi } from '../../services/admin/adminApi';

const Connection: React.FC = () => {
  const { isDark } = useTheme();
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await adminApi.getWhatsAppStatus();
      setStatus(result);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Falha ao obter status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    // Auto-refresh: 5s para QR (scan rapido), 30s para conectado
    const getRefreshInterval = () => {
      if (status?.status === 'qr' || status?.status === 'connecting') {
        return 5000; // 5 segundos
      }
      return 30000; // 30 segundos
    };

    const interval = setInterval(fetchStatus, getRefreshInterval());
    return () => clearInterval(interval);
  }, [fetchStatus, status?.status]);

  const handleAction = async (action: string, apiCall: () => Promise<any>) => {
    setActionLoading(action);
    setError(null);
    try {
      await apiCall();
      await fetchStatus();
    } catch (err: any) {
      setError(err.message || `Falha ao ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConnect = () => handleAction('connect', () => adminApi.connectWhatsApp());
  const handleReconnect = () => handleAction('reconnect', () => adminApi.reconnectWhatsApp());
  const handleDisconnect = () => handleAction('disconnect', () => adminApi.disconnectWhatsApp());

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    await handleAction('delete', () => adminApi.deleteWhatsAppSession());
  };

  const getStatusConfig = (statusValue: ConnectionStatus) => ({
    connected: {
      color: 'text-green-500',
      bg: 'bg-green-100 dark:bg-green-900/30',
      label: 'Conectado',
      icon: 'check'
    },
    connecting: {
      color: 'text-yellow-500',
      bg: 'bg-yellow-100 dark:bg-yellow-900/30',
      label: 'Conectando...',
      icon: 'spinner'
    },
    disconnected: {
      color: 'text-red-500',
      bg: 'bg-red-100 dark:bg-red-900/30',
      label: 'Desconectado',
      icon: 'x'
    },
    qr: {
      color: 'text-blue-500',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      label: 'Aguardando QR Code',
      icon: 'qr'
    },
    error: {
      color: 'text-red-500',
      bg: 'bg-red-100 dark:bg-red-900/30',
      label: 'Erro',
      icon: 'error'
    },
  }[statusValue] || {
    color: 'text-gray-500',
    bg: 'bg-gray-100 dark:bg-gray-700',
    label: 'Desconhecido',
    icon: 'unknown'
  });

  const renderIcon = (iconType: string, colorClass: string) => {
    switch (iconType) {
      case 'check':
        return (
          <svg className={`w-12 h-12 ${colorClass}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        );
      case 'qr':
        return (
          <svg className={`w-12 h-12 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
        );
      case 'spinner':
        return (
          <svg className={`w-12 h-12 ${colorClass} animate-spin`} fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'error':
        return (
          <svg className={`w-12 h-12 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      default:
        return (
          <svg className={`w-12 h-12 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        );
    }
  };

  const ButtonSpinner = () => (
    <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const config = status ? getStatusConfig(status.status) : getStatusConfig('disconnected');
  const currentStatus = status?.status || 'disconnected';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Conexao WhatsApp
        </h1>
        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Gerencie a conexao do bot com o WhatsApp (apiwts.top)
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Status Card */}
      <div className={`rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} p-8`}>
        <div className="text-center">
          {/* Status Icon */}
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${config.bg} mb-6`}>
            {renderIcon(config.icon, config.color)}
          </div>

          {/* Status Label */}
          <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {config.label}
          </h2>

          {/* Phone Number */}
          {status?.number && currentStatus === 'connected' && (
            <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              {status.number}
            </p>
          )}

          {/* Session ID */}
          {status?.sessionId && (
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-2`}>
              Sessao: {status.sessionId}
            </p>
          )}

          {/* QR Code Display */}
          {currentStatus === 'qr' && status?.qr && (
            <div className="my-8">
              <div className={`inline-block p-4 rounded-lg ${isDark ? 'bg-white' : 'bg-gray-50'}`}>
                <img src={status.qr} alt="QR Code" className="w-64 h-64" />
              </div>
              <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Escaneie o QR Code com seu WhatsApp
              </p>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Atualizando automaticamente a cada 5 segundos...
              </p>
            </div>
          )}

          {/* Last Connected */}
          {status?.lastConnected && (
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} mb-6`}>
              Ultima atividade: {new Date(status.lastConnected).toLocaleString('pt-BR')}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {/* Connect Button - show when disconnected or error */}
            {(currentStatus === 'disconnected' || currentStatus === 'error') && (
              <button
                onClick={handleConnect}
                disabled={actionLoading !== null}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                  actionLoading === 'connect'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {actionLoading === 'connect' ? (
                  <>
                    <ButtonSpinner />
                    Conectando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    Nova Conexao
                  </>
                )}
              </button>
            )}

            {/* Reconnect Button - show when disconnected or connected (for refresh) */}
            {(currentStatus === 'disconnected' || currentStatus === 'connected') && (
              <button
                onClick={handleReconnect}
                disabled={actionLoading !== null}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                  actionLoading === 'reconnect'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } text-white`}
              >
                {actionLoading === 'reconnect' ? (
                  <>
                    <ButtonSpinner />
                    Reconectando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reconectar
                  </>
                )}
              </button>
            )}

            {/* Disconnect Button - show when connected */}
            {currentStatus === 'connected' && (
              <button
                onClick={handleDisconnect}
                disabled={actionLoading !== null}
                className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                  actionLoading === 'disconnect'
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                } text-white`}
              >
                {actionLoading === 'disconnect' ? (
                  <>
                    <ButtonSpinner />
                    Desconectando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Desconectar
                  </>
                )}
              </button>
            )}

            {/* Delete Session Button - always show (danger zone) */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={actionLoading !== null}
              className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center ${
                actionLoading === 'delete'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              } text-white`}
            >
              {actionLoading === 'delete' ? (
                <>
                  <ButtonSpinner />
                  Deletando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Deletar Sessao
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md mx-4`}>
            <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Confirmar Exclusao
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Tem certeza que deseja deletar a sessao do WhatsApp? Esta acao nao pode ser desfeita e voce precisara escanear um novo QR Code para reconectar.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Sim, Deletar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className={`rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm p-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Como conectar
        </h3>
        <ol className={`list-decimal list-inside space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          <li>Clique em "Nova Conexao" para gerar o QR Code</li>
          <li>Abra o WhatsApp no seu celular</li>
          <li>Toque em Menu ou Configuracoes e selecione "Dispositivos conectados"</li>
          <li>Toque em "Conectar um dispositivo"</li>
          <li>Aponte seu celular para o QR Code acima</li>
        </ol>

        <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <strong>Dica:</strong> Se a conexao cair frequentemente, use "Reconectar".
            Use "Deletar Sessao" apenas se precisar conectar um numero diferente.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Connection;
